import type { ReactNode } from "react";
import { formatDate } from "../../../lib/format-date";
import { addressLine, asDetails, type Details } from "./detail-values";

/**
 * Building blocks for the per-type credential detail views.
 *
 * Every renderer composes these rather than styling its own rows, so a
 * malpractice policy and a state licence still look like the same product even
 * though their contents have almost nothing in common.
 *
 * All of them render nothing for an absent value. A credential's `details` is a
 * sparse bag — most types carry a handful of the fields they allow — and a
 * popover padded with "—" hides the few facts that are actually recorded.
 */

const isEmpty = (value: unknown) =>
  value === null ||
  value === undefined ||
  value === "" ||
  (Array.isArray(value) && value.length === 0);

/** One label/value line. */
export function Row({
  label,
  value,
}: {
  label: string;
  value: ReactNode | string | number | boolean | null | undefined;
}) {
  if (isEmpty(value)) {
    return null;
  }
  const rendered =
    typeof value === "boolean" ? (value ? "Yes" : "No") : (value as ReactNode);
  return (
    <div className="flex gap-3 text-sm">
      <dt className="w-32 shrink-0 text-slate-500">{label}</dt>
      <dd className="min-w-0 flex-1 break-words text-slate-900">{rendered}</dd>
    </div>
  );
}

export function DateRow({ label, value }: { label: string; value: unknown }) {
  if (isEmpty(value)) {
    return null;
  }
  return <Row label={label} value={formatDate(String(value))} />;
}

/** A run of short values — DEA schedules, compact states, taxonomy codes. */
export function ChipsRow({
  label,
  values,
}: {
  label: string;
  values: unknown;
}) {
  const list = Array.isArray(values) ? values.filter(Boolean) : [];
  if (list.length === 0) {
    return null;
  }
  return (
    <div className="flex gap-3 text-sm">
      <dt className="w-32 shrink-0 text-slate-500">{label}</dt>
      <dd className="flex min-w-0 flex-1 flex-wrap gap-1">
        {list.map((value) => (
          <span
            key={String(value)}
            className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700"
          >
            {String(value)}
          </span>
        ))}
      </dd>
    </div>
  );
}

export type DetailRow = {
  label: string;
  value: ReactNode | string | number | boolean | null | undefined;
};

/**
 * A titled group, rendered only when at least one of its rows has a value.
 *
 * Takes row DATA rather than children on purpose. An earlier version accepted
 * JSX and tried `children.some(Boolean)` to decide whether to render — but a
 * `<Row>` element is truthy even when it renders null, so every section drew
 * its header and a provider with no military service still got a "Military
 * service" heading over nothing.
 */
export function Section({ title, rows }: { title: string; rows: DetailRow[] }) {
  const present = rows.filter((row) => !isEmpty(row.value));
  if (present.length === 0) {
    return null;
  }
  return (
    <div className="border-t pt-2">
      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {title}
      </p>
      <dl className="space-y-1.5">
        {present.map((row) => (
          <Row key={row.label} label={row.label} value={row.value} />
        ))}
      </dl>
    </div>
  );
}

export function Fields({ children }: { children: ReactNode }) {
  return <dl className="space-y-1.5">{children}</dl>;
}

/** The shared address shape used by education, training, work and insurance. */
export function AddressRow({
  label,
  address,
}: {
  label: string;
  address: unknown;
}) {
  const line = addressLine(address);
  if (!line) {
    return null;
  }
  return <Row label={label} value={line} />;
}

/** Repeating records — malpractice claims, USMLE steps. */
export function RepeatingSection({
  title,
  items,
  renderItem,
}: {
  title: string;
  items: unknown;
  renderItem: (item: Details, index: number) => ReactNode;
}) {
  const list = Array.isArray(items) ? items : [];
  if (list.length === 0) {
    return null;
  }
  return (
    <div className="border-t pt-2">
      <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {title} ({list.length})
      </p>
      <div className="space-y-2">
        {list.map((item, index) => (
          <div
            key={index}
            className="rounded border border-slate-100 bg-slate-50 p-2"
          >
            <dl className="space-y-1">{renderItem(asDetails(item), index)}</dl>
          </div>
        ))}
      </div>
    </div>
  );
}
